import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Pencil,
  Plus,
  Phone,
  MapPin,
  Briefcase,
  UserCheck,
  Calendar,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Card, CardBody, CardHeader } from '@/components/common/Card'
import { Badge } from '@/components/common/Badge'
import { Loader, EmptyState } from '@/components/common/Loader'
import { Modal } from '@/components/common/Modal'
import { PatientAvatar } from '@/components/patient/PatientAvatar'
import { EditPersonalInfoModal } from '@/components/patient/EditPersonalInfoModal'
import { VisitDetailsView } from '@/components/patient/VisitDetailsView'
import { PatientBillsSection } from '@/components/patient/PatientBillsSection'
import { patientApi } from '@/api/patientApi'
import { visitApi } from '@/api/visitApi'
import { usePatientStore } from '@/store/patientStore'
import { FEATURES } from '@/lib/features'
import {
  calculateAge,
  formatDate,
  formatMobile,
  humanize,
} from '@/lib/utils'

export default function PatientDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const remove = usePatientStore((s) => s.remove)

  const [patient, setPatient] = useState(null)
  const [visits, setVisits] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null) // 'not_found' | 'backend' | null
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setLoadError(null)
    // The patient fetch decides the page state; visits are optional and
    // should never break the page if they fail.
    patientApi
      .get(id)
      .then((p) => {
        if (!alive) return
        setPatient(p)
        // Now try to load visits — if this fails, the page still renders.
        return visitApi
          .listForPatient(id, { silent: true })
          .then((vs) => {
            if (!alive) return
            setVisits(Array.isArray(vs) ? vs : vs?.visits || [])
          })
          .catch(() => {
            /* visits are optional — leave empty */
          })
      })
      .catch((err) => {
        if (!alive) return
        const status = err?.response?.status
        setLoadError(status === 404 ? 'not_found' : 'backend')
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [id])

  const handleEditSuccess = (updated) => {
    setPatient(updated)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await patientApi.remove(id)
      remove(id)
      toast.success('Patient deleted')
      navigate('/patients')
    } catch {
      // toast handled
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="container-page py-10">
        <Loader label="Loading patient record…" />
      </div>
    )
  }

  if (!patient) {
    // Distinguish "this patient ID does not exist" (404 / not_found) from
    // "the backend crashed while trying to fetch the patient" (500 / backend).
    // The misleading old behaviour was to always show "Patient not found"
    // even when the patient existed — confusing when the real issue was a
    // backend error.
    const isBackend = loadError === 'backend'
    return (
      <div className="container-page py-10">
        <EmptyState
          title={isBackend ? "Couldn't load patient" : 'Patient not found'}
          description={
            isBackend
              ? 'The backend ran into an error while loading this record. Please try again in a moment.'
              : 'This record may have been deleted or the ID is incorrect.'
          }
          action={
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              {isBackend && (
                <Button onClick={() => window.location.reload()}>Reload</Button>
              )}
              <Button
                variant={isBackend ? 'secondary' : 'primary'}
                onClick={() => navigate('/patients')}
              >
                Back to patient list
              </Button>
            </div>
          }
        />
      </div>
    )
  }

  const age = patient.age ?? calculateAge(patient.dob)

  return (
    <div className="container-page py-6 sm:py-8 lg:py-10">
      <PageHeader
        eyebrow="Patient record"
        title={patient.name}
        description={
          <>
            <span className="font-mono text-primary-700">{patient.patient_id}</span>
            <span className="mx-2 text-ink-300">·</span>
            Registered on {formatDate(patient.registration_date)}
          </>
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <Button
              onClick={() => navigate(`/patients/${id}/visits/new`)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              New visit
            </Button>
          </>
        }
      />

      <div className="mt-7 grid gap-6 lg:grid-cols-3">
        {/* Personal info card */}
        <Card className="lg:col-span-1 lg:sticky lg:top-20 lg:self-start">
          <CardBody className="!pt-7 !pb-6">
            <div className="flex flex-col items-center text-center">
              <PatientAvatar name={patient.name} id={patient.patient_id} size="xl" />
              <h3 className="mt-4 h-display text-xl font-bold text-ink-900">
                {patient.name}
              </h3>
              <div className="mt-1.5 flex flex-wrap items-center justify-center gap-1.5">
                <Badge tone="primary">{age} yrs</Badge>
                <Badge tone="neutral">{humanize(patient.gender)}</Badge>
              </div>
              <p className="mt-2 font-mono text-xs font-bold text-primary-700">
                {patient.patient_id}
              </p>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              {patient.mobile_number && (
                <InfoRow icon={Phone} label="Mobile" value={formatMobile(patient.mobile_number)} />
              )}
              {patient.whatsapp_number && (
                <InfoRow icon={Phone} label="WhatsApp" value={formatMobile(patient.whatsapp_number)} />
              )}
              {patient.address && (
                <InfoRow icon={MapPin} label="Address" value={patient.address} />
              )}
              {patient.occupation && (
                <InfoRow icon={Briefcase} label="Occupation" value={patient.occupation} />
              )}
              {patient.referred_by && (
                <InfoRow icon={UserCheck} label="Referred by" value={patient.referred_by} />
              )}
              <InfoRow icon={Calendar} label="Date of birth" value={formatDate(patient.dob)} />
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <Button
                onClick={() => setEditOpen(true)}
                variant="secondary"
                leftIcon={<Pencil className="h-4 w-4" />}
                className="w-full"
              >
                Update personal information
              </Button>
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="ghost"
                leftIcon={<Trash2 className="h-4 w-4" />}
                className="w-full text-rose-600 hover:bg-rose-50"
              >
                Delete patient
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Visits timeline */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader
              eyebrow="Clinical timeline"
              title={`${visits.length} visit${visits.length === 1 ? '' : 's'} recorded`}
              description="Every consultation is preserved. Click a visit to expand its sections."
              action={
                <Button
                  size="sm"
                  variant="accent"
                  onClick={() => navigate(`/patients/${id}/visits/new`)}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Add visit
                </Button>
              }
            />
            <CardBody className="!pt-0">
              {visits.length === 0 ? (
                <EmptyState
                  icon={<Calendar className="h-6 w-6" />}
                  title="No visits yet"
                  description="Record the first consultation to start the patient's timeline."
                  action={
                    <Button onClick={() => navigate(`/patients/${id}/visits/new`)}>
                      Record first visit
                    </Button>
                  }
                />
              ) : (
                <div className="space-y-3">
                  {visits.map((v, i) => (
                    <VisitDetailsView
                      key={v.visit_id || i}
                      visit={v}
                      defaultOpen={i === 0}
                    />
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {FEATURES.billing && <PatientBillsSection patient={patient} />}        </div>
      </div>

      {/* Modals */}
      <EditPersonalInfoModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        patient={patient}
      />

      <Modal
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        title="Delete patient?"
        description="This permanently removes the patient and every visit. Cannot be undone."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              Yes, delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-600">
          Removing <span className="font-semibold">{patient.name}</span> will erase{' '}
          {visits.length} visit{visits.length === 1 ? '' : 's'} from the system.
        </p>
      </Modal>
    </div>
  )
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-ink-50/50 px-3 py-2.5 ring-1 ring-ink-100">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">
          {label}
        </p>
        <p className="text-sm font-medium text-ink-800 break-words">{value}</p>
      </div>
    </div>
  )
}
